import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import React from 'react';
import { TPagination } from './types';

type RowClickHandler<T> = (result: T) => Promise<void>;

export type TSearchResultProps<T> = {
  results: T[];
  tableHeader: React.ReactNode;
  TableRowComponent: React.FC<{
    result: T;
    index: number;
    onRowClick?: RowClickHandler<T>;
  }>;
  onRowClick?: RowClickHandler<T>;
  pagination?: TPagination;
  onPageChange?: (page: number) => Promise<void>;
  onRowsPerPageChange?: (pageSize: number) => Promise<void>;
  'data-testid'?: string;
};

export default function SearchResult<T>({
  results,
  tableHeader,
  TableRowComponent,
  onRowClick,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  'data-testid': dataTestId,
}: TSearchResultProps<T>) {
  const handleChangePage = async (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    await onPageChange?.(newPage);
  };

  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    await onRowsPerPageChange?.(newPageSize);
  };

  return (
    <Paper data-testid={dataTestId}>
      <TableContainer>
        <Table>
          {tableHeader}
          <TableBody>
            {results.map((result, index) => (
              <React.Fragment key={index}>
                <TableRowComponent result={result} index={index} onRowClick={onRowClick} />
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          component="div"
          count={pagination.totalCount}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          data-testid={dataTestId ? `${dataTestId}-pagination` : undefined}
        />
      )}
    </Paper>
  );
}
